import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, TextField, Switch, FormControlLabel,
  Snackbar, Alert, CircularProgress, InputAdornment,
  Checkbox, Autocomplete
} from '@mui/material';
import { 
  Plus, 
  X, 
  Save, 
  DollarSign, 
  Search, 
  Package, 
  Users,
  FileText,
  Award,
  ToggleLeft
} from 'lucide-react';
import api from '../services/api';
import { styled } from '@mui/material/styles';
import { useRefresh } from '../contexts/RefreshContext';

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    charm_bonus: 0,
    is_commissionable: true,
    status: 'active',
    abbreviation: '',
    eligible_employee_ids_for_charm_bonus: []
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState('');
  const { refreshKey } = useRefresh();

  const fetchEmployeesSimpleList = async () => {
    try {
      const response = await api.get('/api/employees_simple_list');
      setAllEmployees(response.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      showSnackbar('Error loading employee list', 'error');
      setAllEmployees([]);
    }
  };

  useEffect(() => {
    fetchServices();
    fetchEmployeesSimpleList();
  }, []);

  useEffect(() => {
    fetchServices();
  }, [refreshKey]);

  useEffect(() => {
    const query = search.toLowerCase();
    const filtered = services.filter((s) =>
      s.name.toLowerCase().includes(query) ||
      (s.abbreviation && s.abbreviation.toLowerCase().includes(query)) ||
      String(s.id).includes(query)
    );
    setFilteredServices(filtered);
  }, [search, services]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await api.get('/services');
      setServices(response.data.map(s => ({
        ...s,
        eligible_employee_ids_for_charm_bonus: Array.isArray(s.eligible_employee_ids_for_charm_bonus) ? s.eligible_employee_ids_for_charm_bonus : []
      })));
    } catch (error) {
      console.error('Error fetching services:', error);
      showSnackbar('Error loading services', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddService = () => {
    setSelectedService(null);
    setFormData({
      name: '',
      price: '',
      charm_bonus: 0,
      is_commissionable: true,
      status: 'active',
      abbreviation: '',
      eligible_employee_ids_for_charm_bonus: []
    });
    setOpenDialog(true);
  };

  const handleEditService = (service) => {
    setSelectedService(service);
    setFormData({
      name: service.name,
      price: service.price,
      charm_bonus: service.charm_bonus || 0,
      is_commissionable: service.is_commissionable,
      status: service.status || 'active',
      abbreviation: service.abbreviation || '',
      eligible_employee_ids_for_charm_bonus: service.eligible_employee_ids_for_charm_bonus || []
    });
    setOpenDialog(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (e) => {
    setFormData(prev => ({ ...prev, status: e.target.checked ? 'active' : 'inactive' }));
  };

  const handleCharmBonusEligibleEmployeesChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      eligible_employee_ids_for_charm_bonus: newValue.map(employee => employee.id)
    }));
  };

  const handleSaveService = async () => {
    if (!formData.name.trim() || !formData.price) {
      showSnackbar('Name and price are required', 'warning');
      return;
    }

    if (selectedService) {
      const confirmar = window.confirm(`Are you sure you want to save changes to the service "${formData.name}"?`);
      if (!confirmar) return;
    }

    setDialogLoading(true);
    try {
      const serviceData = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        charm_bonus: parseFloat(formData.charm_bonus) || 0,
        is_commissionable: !!formData.is_commissionable,
        eligible_employee_ids_for_charm_bonus: formData.eligible_employee_ids_for_charm_bonus || []
      };

      if (selectedService) {
        await api.put(`/services/${selectedService.id}`, serviceData);
        showSnackbar('Service updated successfully');
      } else {
        await api.post('/services', serviceData);
        showSnackbar('Service added successfully');
      }

      setOpenDialog(false);
      fetchServices();
    } catch (error) {
      console.error('Error saving service:', error);
      showSnackbar(error.response?.data?.msg || 'Error saving service', 'error');
    } finally {
      setDialogLoading(false);
    }
  };
  
  const handleToggleGlobalCommissionable = async (service, isCommissionable) => {
    setLoading(true);
    try {
      const updatedService = {
        ...service,
        is_commissionable: isCommissionable,
      };
  
      await api.put(`/services/${service.id}`, updatedService);
      fetchServices();
      showSnackbar('Commission (general) updated successfully');
    } catch (error) {
      console.error(`Error updating commission (general) for service ${service.id}:`, error);
      showSnackbar('Error updating commission (general)', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (service, isActive) => {
    setLoading(true);
    try {
      const updatedService = {
        ...service,
        status: isActive ? 'active' : 'inactive',
      };
  
      await api.put(`/services/${service.id}`, updatedService);
      fetchServices(); 
      showSnackbar('Status updated successfully');
    } catch (error) {
      console.error(`Error updating status for service ${service.id}:`, error);
      showSnackbar('Error updating status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const AntSwitchPurple = styled(Switch)(({ theme }) => ({
    width: 36, height: 20, padding: 0, display: 'flex',
    '&:active': { '& .MuiSwitch-thumb': { width: 20, }, '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(16px)', }, },
    '& .MuiSwitch-switchBase': { padding: 2, '&.Mui-checked': { transform: 'translateX(16px)', color: '#fff', '& + .MuiSwitch-track': { opacity: 1, backgroundColor: '#8e24aa', }, }, },
    '& .MuiSwitch-thumb': { backgroundColor: '#fff', width: 16, height: 16, borderRadius: 8, },
    '& .MuiSwitch-track': { borderRadius: 10, opacity: 1, backgroundColor: '#E9E9EA', boxSizing: 'border-box', },
  }));

  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 36, height: 20, padding: 0, display: 'flex',
    '&:active': { '& .MuiSwitch-thumb': { width: 20, }, '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(16px)', }, },
    '& .MuiSwitch-switchBase': { padding: 2, '&.Mui-checked': { transform: 'translateX(16px)', color: '#fff', '& + .MuiSwitch-track': { opacity: 1, backgroundColor: theme.palette.mode === 'dark' ? '#177ddc' : '#1890ff', }, }, },
    '& .MuiSwitch-thumb': { backgroundColor: '#fff', width: 16, height: 16, borderRadius: 8, },
    '& .MuiSwitch-track': { borderRadius: 20 / 2, opacity: 1, backgroundColor: theme.palette.mode === 'dark' ? '#39393D' : '#E9E9EA', boxSizing: 'border-box', },
  }));

  return (
    <Box p={1}>
      <Box sx={{ pb: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 1, maxWidth: 1000, margin: '0 auto', }}>
        <TextField size='small' placeholder="Search service..." value={search} onChange={(e) => setSearch(e.target.value)} variant="outlined"
          sx={{ flex: 7, backgroundColor: '#fff', borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', width: { xs: '100%', sm: '70%' }, '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none', }, }, }}
          InputProps={{ startAdornment: ( <InputAdornment position="start"> <Search size={18} /> </InputAdornment> ), }} />
        <Button variant="contained" onClick={handleAddService} startIcon={<Plus size={18} />}
          sx={{ flex: 3, height: '40px', whiteSpace: 'nowrap', width: { xs: '100%', sm: '30%' }, background: 'linear-gradient(to right, #1976d2, #0d47a1)', color: '#fff', fontWeight: 'bold', '&:hover': { background: 'linear-gradient(to right, #1565c0, #003c8f)', }, }}>
          New Service
        </Button>
      </Box>

      <Box sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', maxWidth: 1000, margin: '0 auto', width: '100%', scrollbarWidth: 'thin', '&::-webkit-scrollbar': { height: '8px'}, '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px'} }}>
        <Paper sx={{ backgroundColor: '#fff', width: '100%', minWidth: 'max-content' }}>
          <Table size="small" sx={{ width: '100%' }}>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{fontWeight: 'bold'}}>ID</TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>Name</TableCell>
                <TableCell align="right" sx={{fontWeight: 'bold'}}>Price</TableCell>
                <TableCell align="right" sx={{fontWeight: 'bold'}}>Fixed Bonus</TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>Commission (%)</TableCell>
                 <TableCell sx={{fontWeight: 'bold', whiteSpace: 'nowrap' }}>Selected Employees</TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && filteredServices.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><CircularProgress size={30} /></TableCell></TableRow>
              ) : filteredServices.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><Typography color="text.secondary">No services found</Typography></TableCell></TableRow>
              ) : (
                filteredServices.map((service) => (
                  <TableRow key={service.id} hover onClick={() => handleEditService(service)} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)', cursor: 'pointer'} }}>
                    <TableCell>{service.id}</TableCell>
                    <TableCell>{service.name}</TableCell>
                    <TableCell align="right">${parseFloat(service.price).toFixed(2)}</TableCell>
                    <TableCell align="right">${parseFloat(service.charm_bonus || 0).toFixed(2)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <AntSwitchPurple checked={service.is_commissionable} onChange={(e) => handleToggleGlobalCommissionable(service, e.target.checked)} />
                    </TableCell>
                                        <TableCell sx={{fontSize: '0.75rem', color: service.eligible_employee_ids_for_charm_bonus?.length > 0 ? 'green' : 'text.secondary'}}>
                        {service.eligible_employee_ids_for_charm_bonus?.length > 0 
                            ? `${service.eligible_employee_ids_for_charm_bonus.length} employee(s)`
                            : 'None'}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <AntSwitch checked={service.status === 'active'} onChange={(e) => handleToggleStatus(service, e.target.checked)} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* Modernized Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            fontWeight: 'bold', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Package size={24} color="#1976d2" />
            <span>{selectedService ? 'Edit Service' : 'New Service'}</span>
          </Box>
          {dialogLoading && <CircularProgress size={24} />}
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: '#333' }}>
            Service Information
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <TextField 
              required 
              fullWidth 
              label="Service Name" 
              name="name" 
              value={formData.name} 
              onChange={handleFormChange} 
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Package size={18} color="#666" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField 
              fullWidth 
              label="Abbreviation" 
              name="abbreviation" 
              value={formData.abbreviation} 
              onChange={handleFormChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FileText size={18} color="#666" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField 
              required 
              fullWidth 
              label="Price" 
              name="price" 
              type="number" 
              value={formData.price} 
              onChange={handleFormChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DollarSign size={18} color="#666" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField 
              fullWidth 
              label="Fixed Bonus" 
              name="charm_bonus" 
              type="number" 
              value={formData.charm_bonus} 
              onChange={handleFormChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Award size={18} color="#666" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: '#333' }}>
            Configuration
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <FormControlLabel 
              control={
                <Switch 
                  checked={formData.is_commissionable} 
                  onChange={(e) => setFormData(prev => ({ ...prev, is_commissionable: e.target.checked }))} 
                  color="primary" 
                />
              } 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 
                  <span>Commissionable</span>
                </Box>
              }
              sx={{ margin: 0 }}
            />

            <FormControlLabel 
              control={
                <Switch 
                  checked={formData.status === 'active'} 
                  onChange={handleStatusChange} 
                  color="primary" 
                />
              } 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  
                  <span>Active Service</span>
                </Box>
              }
              sx={{ margin: 0 }}
            />
          </Box>

          <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: '#333' }}>
            Bonus Eligibility
          </Typography>
          
          <Autocomplete
            multiple
            id="charm-bonus-employees"
            options={allEmployees}
            getOptionLabel={(option) => option.name}
            value={allEmployees.filter(emp => formData.eligible_employee_ids_for_charm_bonus.includes(emp.id))}
            onChange={handleCharmBonusEligibleEmployeesChange}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Select Employees"
                placeholder="Choose employees eligible for bonus"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <Users size={18} color="#666" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.name} (ID: {option.id})
              </li>
            )}
            fullWidth
          />
        </DialogContent>

        {/* Action Buttons */}
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            startIcon={<X size={18} />}
            fullWidth
            sx={{
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
            }}
          >
            Cancel
          </Button>

          <Button 
            onClick={handleSaveService} 
            variant="contained" 
            startIcon={<Save size={18} />} 
            disabled={dialogLoading}
            fullWidth
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(to right, #1976d2, #0d47a1)',
              color: '#fff',
              '&:hover': {
                background: 'linear-gradient(to right, #1565c0, #003c8f)',
              },
            }}
          >
            {dialogLoading ? <CircularProgress size={24} color="inherit" /> : 'Save Service'}
          </Button>
        </Box>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ServicesPage;